import { useEffect, useMemo, useState } from 'react';
import {
  DocumentationLinkFilterInputObject,
  DocumentationLinkLinkableTypeEnum,
  EdmDocumentationLinksQuery,
  EdmDocumentationLinksQueryVariables,
  useEdmDocumentationLinksQuery,
} from 'graphql-common';

const PAGE_LIMIT = 1000;

type LinkRow = {
  documentationId: string;
  linkableId: string;
  linkableType: DocumentationLinkLinkableTypeEnum;
  visibleViaQr: boolean;
};

const extractCollection = (
  result: EdmDocumentationLinksQuery | undefined,
): LinkRow[] => (result?.data?.collection ?? []) as unknown as LinkRow[];

type FindBy = EdmDocumentationLinksQueryVariables['findBy'];

interface AccumulatedState {
  cacheKey: string;
  // Map keyed by whichever id we accumulate on (linkableId or documentationId,
  // depending on which anchor the caller is using).
  rowsByKey: Map<string, LinkRow>;
}

/**
 * Loads `DocumentationLink` rows matching `findBy` for a specific batch of
 * ids on each call. Designed for picker modals: the host passes the currently
 * visible table page ids, the hook fetches only those links via the
 * `linkableId`/`documentationId` `IN` filter, and the resulting rows are
 * cached in component state so the visibility column stays consistent when
 * the user paginates back or filters.
 *
 * `cacheKey` identifies the anchor (e.g. `doc:<id>`); when it changes the
 * accumulated cache resets so we don't bleed values across different
 * documentations / linkables.
 *
 * Skipped when no anchor or no visible ids — the caller's `getInitialValue`
 * falls back to `defaultValue: true`. Free-plan `UPGRADE_PLAN_REQUIRED` and
 * not-found errors collapse to empty rows so the picker still mounts.
 */
function useDocumentationLinkRowsBatched({
  findBy,
  cacheKey,
  ids,
  filterBuilder,
  rowKey,
  skip,
}: {
  findBy: FindBy;
  cacheKey: string;
  ids: string[];
  filterBuilder: (idList: string[]) => DocumentationLinkFilterInputObject[];
  rowKey: (row: LinkRow) => string;
  skip: boolean;
}): { rows: LinkRow[]; loading: boolean } {
  const [accumulated, setAccumulated] = useState<AccumulatedState>(() => ({
    cacheKey,
    rowsByKey: new Map(),
  }));

  // Reset the cache when the anchor (documentationId / linkable) changes.
  // Doing it during render avoids stale data leaking into the next fetch.
  if (accumulated.cacheKey !== cacheKey) {
    setAccumulated({ cacheKey, rowsByKey: new Map() });
  }

  const resolvedMap = useMemo(
    () =>
      accumulated.cacheKey === cacheKey
        ? accumulated.rowsByKey
        : new Map<string, LinkRow>(),
    [accumulated, cacheKey],
  );

  // Only ask BE for IDs we haven't already resolved this session.
  const idsToFetch = useMemo(() => {
    if (skip) return [] as string[];
    return ids.filter((id) => id && !resolvedMap.has(id));
    // resolvedMap identity changes when state updates, which is exactly when
    // we want to re-evaluate the remaining ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, ids, resolvedMap]);

  const filters = useMemo<
    DocumentationLinkFilterInputObject[] | undefined
  >(() => {
    if (!idsToFetch.length) return undefined;
    return filterBuilder(idsToFetch);
    // filterBuilder identity isn't stable in callers, but the input it
    // operates on (idsToFetch) is — re-deriving here is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsToFetch]);

  const queryResult = useEdmDocumentationLinksQuery({
    fetchPolicy: 'cache-and-network',
    skip: skip || !filters,
    variables: {
      findBy,
      filters,
      page: 1,
      limit: PAGE_LIMIT,
    },
  });

  // Fold incoming rows into the accumulated map. Returning the same `prev`
  // when nothing changes (already cached, matching values) keeps the hook
  // stable when Apollo's `cache-and-network` policy yields the same data.
  useEffect(() => {
    if (skip || !queryResult.data) return;
    const newRows = extractCollection(queryResult.data);
    if (!newRows.length) return;
    setAccumulated((prev) => {
      if (prev.cacheKey !== cacheKey) return prev;
      let mutated = false;
      const next = new Map(prev.rowsByKey);
      newRows.forEach((row) => {
        const key = rowKey(row);
        const existing = next.get(key);
        if (!existing || existing.visibleViaQr !== row.visibleViaQr) {
          next.set(key, row);
          mutated = true;
        }
      });
      return mutated ? { cacheKey, rowsByKey: next } : prev;
    });
    // rowKey identity isn't stable in callers, but only `queryResult.data`
    // and `cacheKey` meaningfully drive folding new rows in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data, cacheKey, skip]);

  const rows = useMemo(() => Array.from(resolvedMap.values()), [resolvedMap]);

  return {
    rows,
    loading: !skip && !!filters && queryResult.loading,
  };
}

export interface UseDocumentationLinksByDocumentationOptions {
  documentationId: string | undefined;
  visibleLinkableIds: string[];
  skip?: boolean;
}

export interface DocumentationLinksByDocumentationResult {
  byAssetId: Record<string, boolean>;
  byAreaId: Record<string, boolean>;
  byFormTemplateId: Record<string, boolean>;
  loading: boolean;
}

/**
 * Per-page documentation→linkable visibility resolver, anchored by
 * documentation. Pass the currently visible asset / area ids from the
 * picker's table and receive the corresponding `visibleViaQr` baseline.
 */
export function useDocumentationLinksByDocumentation({
  documentationId,
  visibleLinkableIds,
  skip,
}: UseDocumentationLinksByDocumentationOptions): DocumentationLinksByDocumentationResult {
  const isSkipped = skip || !documentationId;
  const findBy = useMemo<FindBy>(
    () => ({ documentationId: documentationId ?? '' }),
    [documentationId],
  );
  const cacheKey = `doc:${documentationId ?? ''}`;

  const { rows, loading } = useDocumentationLinkRowsBatched({
    findBy,
    cacheKey,
    ids: visibleLinkableIds,
    filterBuilder: (ids) => [{ linkableId: { predicate: { in: ids } } }],
    rowKey: (row) => row.linkableId,
    skip: isSkipped,
  });

  const { byAssetId, byAreaId, byFormTemplateId } = useMemo(() => {
    const assets: Record<string, boolean> = {};
    const areas: Record<string, boolean> = {};
    const forms: Record<string, boolean> = {};
    rows.forEach((row) => {
      switch (row.linkableType) {
        case DocumentationLinkLinkableTypeEnum.Asset:
          assets[row.linkableId] = row.visibleViaQr;
          break;
        case DocumentationLinkLinkableTypeEnum.SiteArea:
          areas[row.linkableId] = row.visibleViaQr;
          break;
        case DocumentationLinkLinkableTypeEnum.FormTemplate:
          forms[row.linkableId] = row.visibleViaQr;
          break;
        default:
          break;
      }
    });
    return { byAssetId: assets, byAreaId: areas, byFormTemplateId: forms };
  }, [rows]);

  return {
    byAssetId,
    byAreaId,
    byFormTemplateId,
    loading,
  };
}

export interface UseDocumentationLinksByLinkableOptions {
  linkableId: string | undefined;
  linkableType: DocumentationLinkLinkableTypeEnum;
  visibleDocumentationIds: string[];
  skip?: boolean;
}

export interface DocumentationLinksByLinkableResult {
  byDocumentationId: Record<string, boolean>;
  loading: boolean;
}

/**
 * Per-page resolver, anchored by linkable. Pass the documentation ids
 * currently visible in the docs picker and receive their `visibleViaQr`
 * baseline.
 */
export function useDocumentationLinksByLinkable({
  linkableId,
  linkableType,
  visibleDocumentationIds,
  skip,
}: UseDocumentationLinksByLinkableOptions): DocumentationLinksByLinkableResult {
  const isSkipped = skip || !linkableId;
  const findBy = useMemo<FindBy>(
    () => ({
      linkable: { id: linkableId ?? '', type: linkableType },
    }),
    [linkableId, linkableType],
  );
  const cacheKey = `linkable:${linkableType}:${linkableId ?? ''}`;

  const { rows, loading } = useDocumentationLinkRowsBatched({
    findBy,
    cacheKey,
    ids: visibleDocumentationIds,
    filterBuilder: (ids) => [{ documentationId: { predicate: { in: ids } } }],
    rowKey: (row) => row.documentationId,
    skip: isSkipped,
  });

  const byDocumentationId = useMemo(() => {
    const map: Record<string, boolean> = {};
    rows.forEach((row) => {
      map[row.documentationId] = row.visibleViaQr;
    });
    return map;
  }, [rows]);

  return {
    byDocumentationId,
    loading,
  };
}
